import React, { useCallback, useEffect, useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
const portfolioImages = [{
  src: 'https://cdn.magicpatterns.com/uploads/2yayeq8eS9g3G7GEvzLVuS/IMG_6570_(1).jpg',
  en: 'Lion & Spartan — Black & Grey',
  es: 'León y Espartano — Negro y Gris'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/iUfTjzz3Brspepm26BZo8p/IMG_3919.jpg',
  en: 'Portrait — Photorealism',
  es: 'Retrato — Fotorrealismo'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/mdjhNkvJxWgSanCeDfrSnG/PhotoRoom-20230520_184213-01.jpg',
  en: 'Havana Cuba — Full Sleeve',
  es: 'La Habana Cuba — Manga Completa'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/qumJqoKWa21NpDsujb6YpZ/IMG_2631.jpg',
  en: 'Gothic Architecture — Full Sleeve',
  es: 'Arquitectura Gótica — Manga Completa'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/9AAjUgnxc1Ji874Y7xvjiY/IMG_1565.jpg',
  en: 'Woman & Eagle — Realism',
  es: 'Mujer y Águila — Realismo'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/oCXsoJtCV6dkhXh3eq1may/IMG_1567.jpg',
  en: 'Zeus — Black & Grey',
  es: 'Zeus — Negro y Gris'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/13uCe54C3Y55jMYLXamCJ6/IMG_4963.jpg',
  en: 'Statue of Liberty — Realism',
  es: 'Estatua de la Libertad — Realismo'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/pDYdXcaD7eWZqoMxMrXoKA/IMG_8284.jpg',
  en: 'Ravens & Crystals — Neo Traditional',
  es: 'Cuervos y Cristales — Neo Tradicional'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/aRpedt2PY8o9LqtS1iEN9g/IMG_3908.jpg',
  en: 'Elder & Shepherd — Leg Sleeve',
  es: 'Anciano y Pastor — Manga de Pierna'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/mMji7JAUwNc5MuoKT5w48D/IMG_3329.jpg',
  en: 'Portrait — Fine Art Realism',
  es: 'Retrato — Realismo Fine Art'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/vbZ9jwvHfXxYWsBoDTNWAH/IMG_1796.jpg',
  en: 'Owl — Hand Tattoo',
  es: 'Búho — Tatuaje de Mano'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/jq5fDCNrnvNBuf5RD8T8n8/IMG_8086.jpg',
  en: 'Memento Mori — Chest Piece',
  es: 'Memento Mori — Pecho Completo'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/pFRcNyEc7cA5g96dMX6RKS/IMG_6464.jpg',
  en: 'Egyptian Gods — Chest Piece',
  es: 'Dioses Egipcios — Pecho Completo'
}];
export function PortfolioCarousel() {
  const {
    language
  } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [itemsPerView, setItemsPerView] = useState(3);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const content = {
    en: {
      label: 'PORTFOLIO',
      headline: 'The Work Speaks For Itself',
      btn: 'See More on Instagram',
      btnTiktok: 'See More on TikTok',
      btnFacebook: 'See More on Facebook'
    },
    es: {
      label: 'PORTAFOLIO',
      headline: 'El Trabajo Habla Por Sí Solo',
      btn: 'Ver Más en Instagram',
      btnTiktok: 'Ver Más en TikTok',
      btnFacebook: 'Ver Más en Facebook'
    }
  };
  const t = content[language];
  const totalItems = portfolioImages.length;
  useEffect(() => {
    const handleResize = () => {
      setItemsPerView(window.innerWidth < 768 ? 1 : 3);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const maxIndex = Math.max(0, totalItems - itemsPerView);
  const nextSlide = useCallback(() => {
    setCurrentIndex(prev => prev >= maxIndex ? 0 : prev + 1);
  }, [maxIndex]);
  const prevSlide = () => {
    setCurrentIndex(prev => prev === 0 ? maxIndex : prev - 1);
  };
  useEffect(() => {
    if (!isHovered) {
      const timer = setInterval(nextSlide, 3000);
      return () => clearInterval(timer);
    }
  }, [isHovered, nextSlide]);
  const openLightbox = (idx: number) => {
    setLightboxIndex(idx);
    document.body.style.overflow = 'hidden';
  };
  const closeLightbox = () => {
    setLightboxIndex(null);
    document.body.style.overflow = '';
  };
  const lightboxNext = () => {
    if (lightboxIndex !== null) {
      setLightboxIndex((lightboxIndex + 1) % portfolioImages.length);
    }
  };
  const lightboxPrev = () => {
    if (lightboxIndex !== null) {
      setLightboxIndex(lightboxIndex === 0 ? portfolioImages.length - 1 : lightboxIndex - 1);
    }
  };
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (lightboxIndex === null) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') lightboxNext();
      if (e.key === 'ArrowLeft') lightboxPrev();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex]);
  return <section className="py-24 md:py-32 px-6 border-b border-brand-gold/30 overflow-hidden" data-id="element-139">
      <div className="max-w-7xl mx-auto" data-id="element-140">
        <motion.div className="mb-16 text-center" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.8
      }} data-id="element-141">
          <span className="text-brand-gold font-sans text-sm tracking-[0.3em] uppercase block mb-4" data-id="element-142">
            {t.label}
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif text-brand-gold" data-id="element-143">
            {t.headline}
          </h2>
        </motion.div>

        <div className="relative mb-12" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} data-id="element-144">
          {/* Carousel Track */}
          <div className="overflow-hidden px-2 py-4" data-id="element-145">
            <motion.div className="flex gap-6" animate={{
            x: `calc(-${currentIndex * (100 / itemsPerView)}% - ${currentIndex * (24 / itemsPerView)}px)`
          }} transition={{
            type: 'tween',
            ease: 'easeInOut',
            duration: 0.5
          }} data-id="element-146">
              {portfolioImages.map((img, idx) => <div key={idx} className="relative flex-none w-full md:w-[calc(33.333%-16px)] aspect-[4/5] bg-brand-charcoal border border-brand-gold/30 group overflow-hidden cursor-pointer" onClick={() => openLightbox(idx)} data-id="element-147">
                  <img src={img.src} alt={img[language]} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" data-id="element-148" />

                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-brand-black/80 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col items-center justify-center gap-3" data-id="element-149">
                    <span className="text-brand-gold font-serif text-xl tracking-widest uppercase text-center px-4" data-id="element-150">
                      {img[language]}
                    </span>
                    <span className="text-brand-white/50 font-sans text-xs tracking-widest uppercase" data-id="element-151">
                      {language === 'en' ? 'Tap to zoom' : 'Toca para ampliar'}
                    </span>
                  </div>
                </div>)}
            </motion.div>
          </div>

          {/* Navigation Controls */}
          <div className="flex items-center justify-center gap-6 mt-8" data-id="element-152">
            <button onClick={prevSlide} className="p-2 border border-brand-gold text-brand-gold rounded-full hover:bg-brand-gold hover:text-brand-black transition-colors" data-id="element-153">
              <ChevronLeft className="w-6 h-6" data-id="element-154" />
            </button>

            <div className="flex gap-2" data-id="element-155">
              {Array.from({
              length: maxIndex + 1
            }).map((_, idx) => <button key={idx} onClick={() => setCurrentIndex(idx)} className={`w-2 h-2 rounded-full transition-all duration-300 ${currentIndex === idx ? 'bg-brand-gold w-6' : 'bg-brand-gold/30'}`} data-id="element-156" />)}
            </div>

            <button onClick={nextSlide} className="p-2 border border-brand-gold text-brand-gold rounded-full hover:bg-brand-gold hover:text-brand-black transition-colors" data-id="element-157">
              <ChevronRight className="w-6 h-6" data-id="element-158" />
            </button>
          </div>
        </div>

        <motion.div className="text-center flex flex-col sm:flex-row gap-4 justify-center items-center" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} data-id="element-159">
          <a href="https://www.instagram.com/krlygoldtattoo?igsh=bHI3MW51NWdnZzk1&utm_source=qr" target="_blank" rel="noopener noreferrer" className="inline-block py-4 px-10 bg-transparent border border-brand-gold text-brand-gold font-serif text-lg tracking-widest uppercase transition-all duration-300 hover:bg-brand-gold hover:text-brand-black active:bg-brand-gold active:text-brand-black" data-id="element-160">
            {t.btn}
          </a>
          <a href="https://www.tiktok.com/@krlygoldtattoo?_r=1&_t=ZT-954UMqLOH9B" target="_blank" rel="noopener noreferrer" className="inline-block py-4 px-10 bg-transparent border border-brand-gold text-brand-gold font-serif text-lg tracking-widest uppercase transition-all duration-300 hover:bg-brand-gold hover:text-brand-black active:bg-brand-gold active:text-brand-black" data-id="element-161">
            {t.btnTiktok}
          </a>
          <a href="https://www.facebook.com/share/1CqVNdK2Fb/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="inline-block py-4 px-10 bg-transparent border border-brand-gold text-brand-gold font-serif text-lg tracking-widest uppercase transition-all duration-300 hover:bg-brand-gold hover:text-brand-black active:bg-brand-gold active:text-brand-black" data-id="element-162">
            {t.btnFacebook}
          </a>
        </motion.div>
      </div>

      {/* Lightbox Modal */}
      <AnimatePresence data-id="element-163">
        {lightboxIndex !== null && <motion.div className="fixed inset-0 z-50 flex items-center justify-center" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-164">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-brand-black/95 backdrop-blur-sm" onClick={closeLightbox} data-id="element-165" />

            {/* Close Button */}
            <button onClick={closeLightbox} className="absolute top-6 right-6 z-10 p-3 text-brand-gold hover:text-brand-white transition-colors" aria-label="Close lightbox" data-id="element-166">
              <X className="w-8 h-8" data-id="element-167" />
            </button>

            {/* Prev Button */}
            <button onClick={lightboxPrev} className="absolute left-4 md:left-8 z-10 p-3 text-brand-gold hover:text-brand-white transition-colors" aria-label="Previous image" data-id="element-168">
              <ChevronLeft className="w-8 h-8" data-id="element-169" />
            </button>

            {/* Next Button */}
            <button onClick={lightboxNext} className="absolute right-4 md:right-8 z-10 p-3 text-brand-gold hover:text-brand-white transition-colors" aria-label="Next image" data-id="element-170">
              <ChevronRight className="w-8 h-8" data-id="element-171" />
            </button>

            {/* Image */}
            <AnimatePresence mode="wait" data-id="element-172">
              <motion.div key={lightboxIndex} className="relative z-10 flex flex-col items-center max-w-[90vw] max-h-[85vh]" initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.9
          }} transition={{
            duration: 0.3
          }} data-id="element-173">
                <img src={portfolioImages[lightboxIndex].src} alt={portfolioImages[lightboxIndex][language]} className="max-w-full max-h-[75vh] object-contain border border-brand-gold/30" data-id="element-174" />
                <p className="mt-4 text-brand-gold font-serif text-lg tracking-widest uppercase text-center" data-id="element-175">
                  {portfolioImages[lightboxIndex][language]}
                </p>
                <p className="mt-1 text-brand-white/40 font-sans text-xs tracking-wider" data-id="element-176">
                  {lightboxIndex + 1} / {portfolioImages.length}
                </p>
              </motion.div>
            </AnimatePresence>
          </motion.div>}
      </AnimatePresence>
    </section>;
}